import React, { FC } from 'react';

import {
  Flex,
  Modal,
  Button,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
} from '@chakra-ui/react';

import { Form, Formik } from 'formik';

import FormikInput from 'components/primitives/FormikInput';
import FormikSelectInput from 'components/primitives/FormikSelectInput';
import FormikDatepicker from 'components/primitives/FormikDatepicker/FormikDatepicker.view';

import { getInitialValues, validationSchema } from './EditImpactReport.utils';

import { EditImpactReportViewProps } from './EditImpactReport.props';
import CopyAsCSv from 'components/primitives/CopyAsCSv';
import { calculateLost } from 'utils/calculateLost';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
  noOfLines: 1,
};

const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const monthsMap: Record<number, string> = {
  1: 'Jan',
  2: 'Feb',
  3: 'Mar',
  4: 'Apr',
  5: 'May',
  6: 'Jun',
  7: 'Jul',
  8: 'Aug',
  9: 'Sep',
  10: 'Oct',
  11: 'Nov',
  12: 'Dec',
};

const EditImpactReportView: FC<EditImpactReportViewProps> = (props) => {
  const {
    isOpen,
    report,
    onClose,
    onSubmit,
    isLoading,
    inventoryReports,
    isLoadingReports,
  } = props;
  const data: any[] = [];
  if (report) {
    data.push(
      [],
      [
        report!.impact?.wd.toString(),
        report!.impact?.co2m.toString(),
        report!.impact?.cos.toString(),
        report!.impact?.er.toString(),
        report!.impact?.wc.toString(),
      ],
      [],
    );
  }

  return (
    <Modal
      size="6xl"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent p="0px 10px" borderRadius="15px">
        <ModalCloseButton _focus={{ outline: 'none' }} top="5" />
        <ModalHeader textAlign="center" fontWeight="600" color="#262626">
          Edit Impact Report
        </ModalHeader>
        <ModalBody>
          <Flex direction="column">
            <Formik
              onSubmit={onSubmit}
              initialValues={getInitialValues(report)}
              validationSchema={validationSchema}>
              {({ isValid, setFieldValue }) => {
                return (
                  <Form>
                    <Flex gap="20px" flexWrap="wrap">
                      <FormikSelectInput
                        isLoading={isLoadingReports}
                        placeholder="Select"
                        label="Select Harvest Summary (*)"
                        containerProps={{ flex: 1, minW: '250px' }}
                        name="id"
                        onSuccess={(_report) => {
                          const report = inventoryReports.find(
                            (r) => r._id === _report,
                          );

                          if (report) {
                            setFieldValue(
                              'customerSiteName',
                              report?.site?.name,
                            );

                            const lostItems = report?.details?.reduce(
                              (acc, cur) => acc + calculateLost(cur),
                              0,
                            );

                            setFieldValue('startDate', report?.startDate);
                            setFieldValue('endDate', report?.endDate);
                            setFieldValue(
                              'single_use_items',
                              report?.details?.reduce(
                                (acc, item) => acc + item.ret,
                                0,
                              ) + (lostItems || 0),
                            );
                          }
                        }}>
                        {inventoryReports?.map((s) => (
                          <option key={s._id} value={s._id}>
                            {s?.name || ''}
                          </option>
                        ))}
                      </FormikSelectInput>

                      <FormikInput
                        isReadOnly
                        name="customerSiteName"
                        label="Customer Site Name"
                        labelProps={labelProps}
                        containerProps={{ flex: 1, minW: '250px' }}
                      />
                    </Flex>

                    <Flex mt="10px" gap="20px" flexWrap="wrap">
                      <FormikInput
                        isReadOnly
                        name="name"
                        type="text"
                        label="Impact Report Name (*)"
                        labelProps={labelProps}
                        containerProps={{ flex: 1, minW: '250px' }}
                      />

                      <FormikDatepicker
                        readOnly
                        name="startDate"
                        label="Start Date (*)"
                        labelProps={labelProps}
                        containerProps={{ flex: 1, minW: '250px' }}
                      />
                      <FormikDatepicker
                        readOnly
                        name="endDate"
                        label="End Date (*)"
                        labelProps={labelProps}
                        containerProps={{ flex: 1, minW: '250px' }}
                      />
                    </Flex>

                    <Flex mt="10px" gap="20px" flexWrap="wrap">
                      <FormikSelectInput
                        isDisabled={true}
                        placeholder="Select"
                        label="Select Month"
                        containerProps={{ flex: 1, minW: '250px' }}
                        name="month">
                        {months?.map((m) => (
                          <option key={m} value={m}>
                            {monthsMap[m] || ''}
                          </option>
                        ))}
                      </FormikSelectInput>

                      <FormikInput
                        isReadOnly
                        placeholder="Select"
                        label="Select Year"
                        containerProps={{ flex: 1, minW: '250px' }}
                        name="year"></FormikInput>
                    </Flex>

                    <Flex mt="10px" gap="20px" flexWrap="wrap">
                      <FormikInput
                        isReadOnly
                        name="single_use_items"
                        label="Single-Use Items Eliminated"
                        labelProps={labelProps}
                        containerProps={{ flex: 1, minW: '250px' }}
                      />

                      <FormikInput
                        type="string"
                        name="wd"
                        label="Pounds of Waste Diverted (*)"
                        labelProps={labelProps}
                        containerProps={{ flex: 1, minW: '250px' }}
                      />

                      <FormikInput
                        type="string"
                        name="co2m"
                        label="Pounds of CO₂ Mitigated (*)"
                        labelProps={labelProps}
                        containerProps={{ flex: 1, minW: '250px' }}
                      />
                    </Flex>

                    <Flex mt="10px" gap="20px" flexWrap="wrap">
                      <FormikInput
                        type="string"
                        name="wc"
                        label="Gallons of Water Conserved (*)"
                        labelProps={labelProps}
                        containerProps={{ flex: 1, minW: '250px' }}
                      />

                      <FormikInput
                        type="string"
                        name="er"
                        label="KWh of Energy Reduced (*)"
                        labelProps={labelProps}
                        containerProps={{ flex: 1, minW: '250px' }}
                      />

                      <FormikInput
                        type="string"
                        name="cos"
                        label="Liters of Crude Oil Saved (*)"
                        labelProps={labelProps}
                        containerProps={{ flex: 1, minW: '250px' }}
                      />
                    </Flex>

                    <Flex my={8} flexDirection="row" justifyContent="center">
                      <Button
                        variant="outline"
                        marginRight="10px"
                        w="134px"
                        onClick={onClose}
                        isDisabled={isLoading}>
                        Cancel
                      </Button>
                      <Button
                        isDisabled={!isValid}
                        w="134px"
                        marginRight="10px"
                        type="submit"
                        isLoading={isLoading}>
                        Update
                      </Button>

                      <CopyAsCSv data={data} title="Copy with format" />
                    </Flex>
                  </Form>
                );
              }}
            </Formik>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default EditImpactReportView;
